import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// ============================================================================
// Server-DTOer for 3D lagerlayout (speiler WarehouseLayoutDTOs.cs)
// ============================================================================

export interface ServerWarehouseLayout {
  warehouseID: string;
  layoutName?: string | null;
  lengthMeters: number;
  widthMeters: number;
  heightMeters: number;
  gridUnitMeters: number;
  hasBackgroundImage: boolean;
  backgroundImageType?: string | null;
  backgroundScale?: number | null;
  backgroundOffsetX?: number | null;
  backgroundOffsetY?: number | null;
  lastUpdate?: string | null;
  updatedBy?: string | null;
}

export interface ServerWarehouseLevel {
  levelNo: number;
  levelName: string;
  elevationMeters: number;
  floorPolygon?: string | null;
}

export interface ServerRackPosition {
  rackNumber: string;
  levelNo: number;
  positionX: number;
  positionY: number;
  rotation: number;
  rackLabel?: string | null;
  color?: string | null;
  widthOverride?: number | null;
  depthOverride?: number | null;
  heightOverride?: number | null;
}

export interface ServerBinPosition {
  binCode: string;
  levelNo: number;
  placementKind: 'FLOOR' | 'WALL' | 'HANGING' | 'CABINET' | 'DOCK';
  positionX: number;
  positionY: number;
  rotation: number;
  widthOverride?: number | null;
  depthOverride?: number | null;
  heightOverride?: number | null;
  color?: string | null;
}

export interface ServerLayoutObject {
  id: number;
  levelNo: number;
  objectType: string;
  objectName?: string | null;
  positionX: number;
  positionY: number;
  width: number;
  depth: number;
  height?: number | null;
  rotation: number;
  color?: string | null;
  stcZoneID?: string | null;
}

export interface FullServerLayout {
  layout: ServerWarehouseLayout | null;
  levels: ServerWarehouseLevel[];
  rackPositions: ServerRackPosition[];
  binPositions: ServerBinPosition[];
  objects: ServerLayoutObject[];
}

export interface ServerRackVerticalGeometry {
  code: string;
  widthMeters: number;
}

export interface ServerRackShelfGeometry {
  code: string;
  heightMeters: number;
}

export interface ServerRackGeometry {
  rackNumber: string;
  widthMeters: number;
  depthMeters: number;
  heightMeters: number;
  verticals: ServerRackVerticalGeometry[];
  shelves: ServerRackShelfGeometry[];
  locationCount: number;
  hasBinSizeData: boolean;
}

export interface ServerSpecialBin {
  binCode: string;
  description1?: string | null;
  stcZoneID?: string | null;
  binSizeNo: number;
  widthMeters?: number | null;
  depthMeters?: number | null;
  heightMeters?: number | null;
}

export interface ServerWarehouseGeometry {
  racks: ServerRackGeometry[];
  specialBins: ServerSpecialBin[];
}

export interface ServerUnplacedItems {
  rackNumbers: string[];
  specialBins: ServerSpecialBin[];
}

export interface UpsertLayoutRequest {
  layoutName?: string | null;
  lengthMeters: number;
  widthMeters: number;
  heightMeters: number;
  gridUnitMeters: number;
  expectedLastUpdate?: string | null;
}

// ============================================================================
// Service
// ============================================================================

class WarehouseLayoutService {
  private async ensureOk(response: Response, action: string): Promise<Response> {
    if (!response.ok) {
      let message = `${action}: ${response.status} ${response.statusText}`;
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch {
        // ignorer — bruk standardmelding
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return response;
  }

  async getFullLayout(warehouseId: string): Promise<FullServerLayout> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}`
    );
    await this.ensureOk(response, 'Kunne ikke hente lagerlayout');
    return response.json();
  }

  async upsertLayout(warehouseId: string, request: UpsertLayoutRequest): Promise<ServerWarehouseLayout> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}`,
      request
    );
    await this.ensureOk(response, 'Kunne ikke lagre lagerlayout');
    return response.json();
  }

  async upsertRackPositions(
    warehouseId: string,
    positions: ServerRackPosition[],
    replaceAll: boolean
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/rack-positions`,
      { positions, replaceAll }
    );
    await this.ensureOk(response, 'Kunne ikke lagre reolposisjoner');
  }

  async upsertBinPositions(
    warehouseId: string,
    positions: ServerBinPosition[],
    replaceAll: boolean
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/bin-positions`,
      { positions, replaceAll }
    );
    await this.ensureOk(response, 'Kunne ikke lagre lokasjonsposisjoner');
  }

  async replaceObjects(
    warehouseId: string,
    objects: Omit<ServerLayoutObject, 'id'>[]
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/objects`,
      objects
    );
    await this.ensureOk(response, 'Kunne ikke lagre layout-objekter');
  }

  async upsertLevels(warehouseId: string, levels: ServerWarehouseLevel[]): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/levels`,
      { levels }
    );
    await this.ensureOk(response, 'Kunne ikke lagre nivåer');
  }

  async getGeometry(warehouseId: string): Promise<ServerWarehouseGeometry> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/geometry`
    );
    await this.ensureOk(response, 'Kunne ikke hente reolgeometri');
    return response.json();
  }

  async getUnplaced(warehouseId: string): Promise<ServerUnplacedItems> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/unplaced`
    );
    await this.ensureOk(response, 'Kunne ikke hente uplasserte elementer');
    return response.json();
  }

  async updateBackground(
    warehouseId: string,
    request: {
      imageBase64?: string | null;
      imageType?: string | null;
      scale?: number | null;
      offsetX?: number | null;
      offsetY?: number | null;
    }
  ): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/background`,
      request
    );
    await this.ensureOk(response, 'Kunne ikke lagre plantegning');
  }

  /** Henter plantegningen som object-URL (kalleren må revoke ved opprydding). */
  async getBackgroundObjectUrl(warehouseId: string): Promise<string | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/warehouse-layout/${encodeURIComponent(warehouseId)}/background`
    );
    if (response.status === 404) return null;
    await this.ensureOk(response, 'Kunne ikke hente plantegning');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}

export const warehouseLayoutService = new WarehouseLayoutService();
